import { useEffect, useState } from 'react';

/** Évènement Chrome/Android d'installation de PWA (non typé par TS par défaut). */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** L'app tourne-t-elle déjà en mode installé (écran d'accueil) ? */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/** Appareil iOS (iPhone/iPad) — l'installation y est manuelle (Partager → …). */
export function isIos(): boolean {
  const ua = window.navigator.userAgent.toLowerCase();
  const iOSDevice = /iphone|ipad|ipod/.test(ua);
  // iPadOS récent se présente comme un Mac tactile.
  const iPadOs =
    window.navigator.platform === 'MacIntel' &&
    (window.navigator as unknown as { maxTouchPoints?: number }).maxTouchPoints !== undefined &&
    ((window.navigator as unknown as { maxTouchPoints?: number }).maxTouchPoints ?? 0) > 1;
  return iOSDevice || iPadOs;
}

export interface InstallPrompt {
  /** Installation programmable disponible (Android/Chrome). */
  canInstall: boolean;
  /** Déclenche l'invite d'installation native. Renvoie true si acceptée. */
  promptInstall: () => Promise<boolean>;
  /** App déjà installée / lancée en mode standalone. */
  installed: boolean;
  /** Plateforme iOS (installation manuelle). */
  ios: boolean;
}

/**
 * Expose l'état d'installation de la PWA : capture `beforeinstallprompt`
 * (Android/Chrome) pour proposer un vrai bouton « Télécharger l'app », et
 * détecte iOS (installation manuelle) et le mode déjà installé.
 */
export function useInstallPrompt(): InstallPrompt {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(() => isStandalone());

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = async (): Promise<boolean> => {
    if (!deferred) return false;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    return choice.outcome === 'accepted';
  };

  return {
    canInstall: !!deferred && !installed,
    promptInstall,
    installed,
    ios: isIos(),
  };
}
